import React, { useState } from "react";
import { ArrowRight, Shuffle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const TYPED_CONTEXTS = ["protocol", "cta", "budget", "study_build", "icf"];

/** Pick the target context and start the network-wide move (documents re-tagged, then the source retires itself). */
export function ContextMovePanel({ ctx, contexts, docs, envCount, onStarted, onCancel }) {
    const [to, setTo] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const targets = (contexts || []).filter((c) => c.active && c.id !== ctx.id);
    const target = targets.find((c) => c.id === to);

    const start = async () => {
        setBusy(true);
        setErr(null);
        try {
            const { data } = await api.post(`/admin/datalake/contexts/${ctx.id}/move`, { to });
            onStarted(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start the move");
            setBusy(false);
        }
    };

    return (
        <div className="rounded border border-[#7C3AED]/30 bg-[#7C3AED]/[0.06] p-3 space-y-3 max-w-full min-w-0" data-testid="move-panel">
            <div className="flex items-center gap-2 text-xs text-white/80">
                <span className="font-mono px-1.5 py-0.5 rounded-sm border border-white/15">{ctx.id}</span>
                <ArrowRight className="w-3.5 h-3.5 text-white/40" />
                <select value={to} onChange={(e) => setTo(e.target.value)} className="flex-1 min-w-0 w-0 bg-black border border-white/15 rounded px-2 py-1 text-xs font-mono focus:border-[#7C3AED] outline-none truncate" data-testid="move-target-select">
                    <option value="">Choose the new context…</option>
                    {targets.map((c) => <option key={c.id} value={c.id}>{c.id} — {c.label}</option>)}
                </select>
            </div>
            <p className="text-[11px] text-white/55 leading-relaxed" data-testid="move-summary">
                {target
                    ? `Replaces “${ctx.label}” with “${target.label}” on ${docs.toLocaleString()} document${docs === 1 ? "" : "s"} in ${envCount} environment${envCount === 1 ? "" : "s"} — other contexts on each document stay. Each spoke moves its own documents with its next sync check (≤ 1 min); “${ctx.label}” is retired automatically once none are left anywhere.`
                    : "Every document carrying this context — in every environment — gets the new context instead, then this context retires itself."}
            </p>
            {err && <div className="text-[11px] text-rose-300" data-testid="move-error">{err}</div>}
            <div className="flex justify-end gap-2">
                <button onClick={onCancel} className="text-xs text-white/60 hover:text-white px-3 py-1.5" data-testid="move-back">Back</button>
                <button onClick={start} disabled={!to || busy} className="inline-flex items-center gap-1.5 text-xs bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-40" data-testid="move-confirm">
                    <Shuffle className="w-3.5 h-3.5" /> {busy ? "Starting…" : "Move & retire"}
                </button>
            </div>
        </div>
    );
}
